import React, { CSSProperties } from 'react';

import { Tooltip } from '@material-ui/core';

import { ApplicationPageTabs } from '../application/application-page';

type IconNameType =
  | ValueOf<typeof LEFT_HAND_NAV_ICON_NAMES>
  | ValueOf<typeof ACCOUNT_NAV_ICON_NAMES>
  | ValueOf<typeof PROFILE_TAB_ICON_NAMES>
  | ValueOf<typeof ICON_NAMES>;

type IconInputProps = {
  iconName: IconNameType;
  /**
   * Optional onClick handler.
   * When provided and not disabled, icon will auto-style with hoverHighlight and cursor pointer.
   */
  onClick?: (e: React.MouseEvent<any, MouseEvent>) => void;
  tooltip?: string;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /**
   * className should be preferred over style prop whenever possible
   * style is available for icon specific tweaks that may need to be made due to svg shape within its bounding box
   */
  style?: CSSProperties;
  /**
   * Prefer using color prop instead of color classes in className to limit our color palette for icons.
   * If you need a color not listed, you should really question if that is a necessary color to use.
   */
  color?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'attention'
    | 'error'
    | 'gray'
    | 'white';
  /**
   * Will disable the onClick handler and change the color to gray
   */
  disabled?: boolean;
};

// Icons from Google Font's Material Design Symbols https://fonts.google.com/icons
export type MaterialIconElement = (props: IconInputProps) => React.ReactElement;

/**
 * All icons should use the MaterialDesignIcon wrapper to provide easy access to shared props for styling.
 * Any icons with specific styling should be created and exported in this file for reusability.
 */
export const MaterialDesignIcon = (props: IconInputProps) => {
  const { iconName, className, size, style, color, disabled, tooltip, onClick } = props;

  let sizeClass;
  switch (size) {
    case 'xs':
      sizeClass = 'material-icon-size-xs';
      break;
    case 'sm':
      sizeClass = 'material-icon-size-sm';
      break;
    case 'lg':
      sizeClass = 'material-icon-size-lg';
      break;
    case 'md':
    default: // Same as md
      sizeClass = 'material-icon-size-md';
  }

  let colorClasses;
  switch (color) {
    case 'primary':
      colorClasses = 'u-color-primary';
      break;
    case 'secondary':
      colorClasses = 'u-color-secondary';
      break;
    case 'success':
      colorClasses = 'u-color-green';
      break;
    case 'attention':
      colorClasses = 'u-color-yellow';
      break;
    case 'error':
      colorClasses = 'u-color-red';
      break;
    case 'gray':
      colorClasses = 'u-color-gray';
      break;
    case 'white':
      colorClasses = 'u-color-white';
      break;
    default:
      colorClasses = 'u-color-inherit';
  }

  const onClickClasses = onClick ? 'u-cursor-pointer typo-link-secondary' : '';
  const disabledClasses = disabled ? 'u-color-gray u-colorHover-transparent u-cursor-default' : '';

  const buttonProps = onClick && !disabled ? { onClick, role: 'presentation' } : {};

  return (
    <Tooltip arrow title={tooltip || ''}>
      <span
        className={`material-symbols-outlined ${sizeClass} ${colorClasses} ${onClickClasses} ${disabledClasses} ${
          className || ''
        }`}
        style={style}
        {...buttonProps}
      >
        {iconName}
      </span>
    </Tooltip>
  );
};

/**
 * All icon names used should be added to the const - this makes it easier to find them and to prevent typos
 * If an icon should be used for a specific purpose, give it a semantic name in a new const (see LEFT_HAND_NAV_ICON_NAMES).
 * Semantic naming is meant to enforce consistency for icon meaning, by clearly using these icons in explicit context
 */
export const ICON_NAMES = {
  ADD: 'add',
  ADD_ALERT: 'add_alert',
  ADD_CIRCLE: 'add_circle',
  ADD_PHOTO_ALTERNATE: 'add_photo_alternate',
  ALARM_ADD: 'alarm_add',
  ARROW_BACK: 'arrow_back',
  ARROW_DROP_DOWN: 'arrow_drop_down',
  ARROW_DROP_UP: 'arrow_drop_up',
  ARROW_FORWARD: 'arrow_forward',
  ARROW_FORWARD_IOS: 'arrow_forward_ios',
  ARROW_LEFT: 'arrow_left',
  ARROW_RIGHT: 'arrow_right',
  ASSIGNMENT_TURNED_IN: 'assignment_turned_in',
  ATTACH_FILE: 'attach_file',
  BACK_ARROW: 'arrow_back',
  BAR_CHART_4_BARS: 'bar_chart_4_bars',
  BLOCK: 'block',
  CACHED: 'cached',
  CALENDAR_TODAY: 'calendar_today',
  CALL: 'call',
  CANCEL: 'cancel',
  CHECK: 'check',
  CHECK_CIRCLE: 'check_circle',
  CHECK_BOX: 'check_box',
  CHEVRON_RIGHT: 'chevron_right',
  CLEAR: 'clear',
  CLOSE: 'close',
  COMMENT: 'comment',
  CONTACT_MAIL: 'contact_mail',
  CONTENT_COPY: 'content_copy',
  CREDIT_CARD: 'credit_card',
  DELETE_FOREVER: 'delete_forever',
  DESCRIPTION: 'description',
  DO_NOT_DISTURB: 'do_not_disturb',
  DO_NOT_DISTURB_ON: 'do_not_disturb_on',
  DOMAIN: 'domain',
  DONE: 'done',
  DOWNLOAD: 'download',
  DRAFT: 'draft',
  EDIT: 'edit',
  ELECTRIC_BOLT: 'electric_bolt',
  EMAIL: 'email',
  ERROR: 'error',
  EVENT: 'event',
  EXPAND_LESS: 'expand_less',
  EXPAND_MORE: 'expand_more',
  EXIT_TO_APP: 'exit_to_app',
  FIBER_MANUAL_RECORD: 'fiber_manual_record',
  FILE_COPY: 'file_copy',
  FOLDER_SHARED: 'folder_shared',
  FORUM: 'forum',
  FULLSCREEN: 'fullscreen',
  FULLSCREEN_EXIT: 'fullscreen_exit',
  GROUP: 'group',
  GROUP_ADD: 'group_add',
  HELP: 'help',
  HOURGLASS: 'hourglass',
  HISTORY: 'history',
  INFO: 'info',
  KEYBOARD_ARROW_DOWN: 'keyboard_Arrow_down',
  KEYBOARD_ARROW_LEFT: 'keyboard_arrow_left',
  KEYBOARD_ARROW_RIGHT: 'keyboard_arrow_right',
  KEYBOARD_ARROW_UP: 'keyboard_arrow_up',
  LINK: 'link',
  LINK_OFF: 'link_off',
  LOCK: 'lock',
  LOCK_OPEN: 'lock_open',
  MAIL: 'mail',
  MANAGE_SEARCH: 'manage_search',
  MENU: 'menu',
  MERGE_TYPE: 'merge_type',
  MONETIZATION_ON: 'monetization_on',
  MORE_VERT: 'more_vert',
  MY_LOCATION: 'my_location',
  NAVIGATE_BEFORE: 'navigate_before',
  NOTIFICATIONS: 'notifications',
  OPEN_IN_NEW: 'open_in_new',
  PAGE_INFO: 'page_info',
  PAUSE_CIRCLE: 'pause_circle',
  PERSON: 'person',
  PERSON_ADD: 'person_add',
  PERSON_SEARCH: 'person_search',
  PHONE: 'phone',
  PHONE_CALLBACK: 'phone_callback',
  PHONE_DISABLED: 'phone_disabled',
  PHONE_FORWARDED: 'phone_forwarded',
  PLAY_CIRCLE: 'play_circle',
  POST_ADD: 'post_add',
  POWER_SETTINGS_NEW: 'power_settings_new',
  PRIORITY_HIGH: 'priority_high',
  RADIO_BUTTON_CHECKED: 'radio_button_checked',
  RADIO_BUTTON_UNCHECKED: 'radio_button_unchecked',
  RECORD_VOICE_OVER: 'record_voice_over',
  REDEEM: 'redeem',
  REFRESH: 'refresh',
  REMOVE: 'remove',
  REPLAY: 'replay',
  RULE: 'rule',
  SAVE: 'save',
  SCHEDULE: 'schedule',
  SCHEDULE_SEND: 'schedule_send',
  SEARCH: 'search',
  SEND: 'send',
  SETTINGS: 'settings',
  SMS: 'sms',
  STAR: 'star',
  SWAP_HORIZ: 'swap_horiz',
  TOUCH_APP: 'touch_app',
  TRENDING_DOWN: 'trending_down',
  TRENDING_UP: 'trending_up',
  UNSUBSCRIBE: 'unsubscribe',
  VERIFIED_USER: 'verified_user',
  VIEW_LIST: 'view_list',
  WARNING: 'warning',
} as const;

export const LEFT_HAND_NAV_ICON_NAMES = {
  ADMIN_PANEL: ICON_NAMES.LOCK,
  ANNOUNCEMENTS: ICON_NAMES.RECORD_VOICE_OVER,
  AUTOMATIONS: ICON_NAMES.ELECTRIC_BOLT,
  COMPANY_SETTINGS: ICON_NAMES.SETTINGS,
  DIRECTORY: ICON_NAMES.FOLDER_SHARED,
  JOBS: ICON_NAMES.PERSON_SEARCH,
  MESSAGES: ICON_NAMES.FORUM,
  REFERRAL_PROGRAM: ICON_NAMES.REDEEM,
  REFERRALS: ICON_NAMES.GROUP_ADD,
  SURVEYS: ICON_NAMES.RULE,
  TAKE_A_TOUR: ICON_NAMES.TOUCH_APP,
} as const;

export const ACCOUNT_NAV_ICON_NAMES = {
  ACCOUNT_PROFILE: ICON_NAMES.PERSON,
  BILLING: ICON_NAMES.CREDIT_CARD,
  CARRIER_REGISTRATION: ICON_NAMES.PHONE,
  ORGANIZATIONS: ICON_NAMES.DOMAIN,
  USERS: ICON_NAMES.GROUP,
} as const;

export const PROFILE_TAB_ICON_NAMES: Record<ApplicationPageTabs | 'HISTORY', string> = {
  ACTIVITY: ICON_NAMES.VIEW_LIST,
  CALLS: ICON_NAMES.CALL,
  COMMENTS: ICON_NAMES.COMMENT,
  DETAILS: ICON_NAMES.PERSON,
  DOCUMENTS: ICON_NAMES.DESCRIPTION,
  FINALIZING_CHECKLIST: ICON_NAMES.CHECK_BOX,
  HISTORY: ICON_NAMES.HISTORY,
  MESSAGES: ICON_NAMES.SMS,
} as const;

// This is an old material-icon icon, but isn't available in material-symbols
// Height and width are set explicitly otherwise the span is as big as the text which makes styling odd
export const AiAutoFixIcon = ({ className }: { className?: string }) => (
  <span className={`material-icons material-icon-size-md ${className || ''}`}>
    auto_fix_high_24px
  </span>
);

/** Specifically styled icons */

export const FilledSmallCircleIcon = (props: Omit<IconInputProps, 'iconName'>) => (
  <MaterialDesignIcon
    iconName={ICON_NAMES.FIBER_MANUAL_RECORD}
    style={{ fontVariationSettings: "'FILL' 1, 'wght' 300" }}
    {...props}
  />
);

export const HeaderButtonAddIcon = () => (
  <MaterialDesignIcon size="sm" iconName={ICON_NAMES.ADD} style={{ marginBottom: '2px' }} />
);

export const JobOverrideIcon = (props: Omit<IconInputProps, 'iconName'>) => (
  <MaterialDesignIcon
    iconName={ICON_NAMES.CONTENT_COPY}
    style={{ width: 42, height: 42, fontSize: 42, fontVariationSettings: "'FILL' 0, 'wght' 200" }}
    {...props}
  />
);
